import type { ReadonlySignal } from '@preact/signals-react';
import type { CommandAction } from 'src/services/CommandPaletteService/protocol';

/**
 * A chord describes a single keystroke plus modifier combination.
 *
 * Either `code` (physical key, e.g. 'Backquote') or `key` (logical value, e.g. 'k') must be
 * provided. `code` is preferred for positional keys; `key` is preferred for letter shortcuts so
 * they follow the user's layout.
 */
export interface KeyChord {
    code?: string;
    key?: string;
    ctrl: boolean;
    shift: boolean;
    alt: boolean;
    meta: boolean;
}

/**
 * A command with a stable identity, a display label, and an action that can be dispatched
 * identically whether it originates from the command palette or a keyboard shortcut.
 *
 * `defaultBinding` is the seed for `KeybindingsService`'s mutable bindings map; commands without
 * a default are unbound by default but can still be bound via settings or surfaced in the palette.
 */
export interface BindableCommand {
    id: string;
    label: string;
    category?: string;
    action: CommandAction;
    defaultBinding?: KeyChord | null;
    /**
     * Optional gate. When false, the dispatcher ignores this binding. The settings UI still
     * shows the command but may render it as disabled. Defaults to always-enabled.
     */
    enabled?: ReadonlySignal<boolean>;
}

export function chordsEqual(a: KeyChord | null | undefined, b: KeyChord | null | undefined): boolean {
    if (a == null || b == null) return a == b;
    return (
        a.code === b.code &&
        a.key === b.key &&
        a.ctrl === b.ctrl &&
        a.shift === b.shift &&
        a.alt === b.alt &&
        a.meta === b.meta
    );
}

export function chordMatchesEvent(chord: KeyChord, event: KeyboardEvent): boolean {
    const keyMatch = chord.code ? event.code === chord.code : event.key === chord.key;
    return (
        keyMatch &&
        event.ctrlKey === chord.ctrl &&
        event.shiftKey === chord.shift &&
        event.altKey === chord.alt &&
        event.metaKey === chord.meta
    );
}

const MODIFIER_KEYS = new Set(['Control', 'Shift', 'Alt', 'Meta']);

/**
 * Build a KeyChord from a captured KeyboardEvent. Returns null if the event is a pure modifier
 * press (e.g. the user is still assembling the chord).
 */
export function chordFromEvent(event: KeyboardEvent): KeyChord | null {
    if (MODIFIER_KEYS.has(event.key)) return null;

    // Prefer `key` for letter/digit keys so layout-dependent shortcuts follow the user's layout.
    // Fall back to `code` for named/physical keys (Escape, Backquote, F-keys, etc.).
    const isPrintable = event.key.length === 1;
    return {
        code: isPrintable ? undefined : event.code,
        key: isPrintable ? event.key.toLowerCase() : undefined,
        ctrl: event.ctrlKey,
        shift: event.shiftKey,
        alt: event.altKey,
        meta: event.metaKey,
    };
}

export function formatChord(chord: KeyChord | null | undefined): string {
    if (!chord) return '';
    const parts: string[] = [];
    if (chord.ctrl) parts.push('Ctrl');
    if (chord.alt) parts.push('Alt');
    if (chord.shift) parts.push('Shift');
    if (chord.meta) parts.push('Meta');
    const leaf = chord.key ?? chord.code ?? '';
    parts.push(leaf.length === 1 ? leaf.toUpperCase() : leaf);
    return parts.join('+');
}
